import { ParsedUrlQuery } from 'querystring';
import { useGetActiveTabFromRouter } from '@hooks';
import { TabPageRedirectRoutes } from '@routes';
import Tabs, { TabsComponentProps } from '@ui/tabs';
import { StyledElement } from '@ui/types';
import { useRouter } from 'next/router';
import { FunctionComponent, ReactNode, useEffect } from 'react';
import { tabQueryKey } from 'src/constant';
import MainLayout from '../main';

export interface TabLayoutProps extends StyledElement {
  tabs: TabsComponentProps;
  filter?: ReactNode;
}

const TabLayout: FunctionComponent<TabLayoutProps> = ({
  children,
  className,
  filter,
  tabs,
}) => {
  return (
    <MainLayout>
      {filter}
      <Tabs className={className} {...tabs} />
      {children}
    </MainLayout>
  );
};

export interface LinkingTabLayoutProps extends TabLayoutProps {
  basePath: string;
  includeQueries?: boolean;
  onCleanQueries?: (
    query: ParsedUrlQuery,
    activeTab: number,
  ) => ParsedUrlQuery | undefined;
}

export const LinkingTabLayout: FunctionComponent<LinkingTabLayoutProps> = ({
  basePath,
  includeQueries,
  onCleanQueries,
  tabs,
  ...props
}) => {
  const router = useRouter();
  const { tabs: tabItems } = tabs;
  const active = useGetActiveTabFromRouter({ tabs: tabItems });

  let query: ParsedUrlQuery | undefined;
  if (includeQueries) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [tabQueryKey]: excludeTab, ...queries } = router.query;
    query = onCleanQueries ? onCleanQueries(queries, active) : queries;
  }

  const onChangeTab = (activeTab: number) => {
    router.push({
      pathname: `${basePath}/${tabItems[activeTab].id}`,
      query,
    });
  };

  useEffect(() => {
    const [pathWithoutQuery] = router.asPath.split('?');
    if (Object.keys(TabPageRedirectRoutes).includes(pathWithoutQuery)) {
      router.push({
        pathname: TabPageRedirectRoutes[pathWithoutQuery],
        query,
      });
    }
  }, [query, router, router.asPath]);

  return (
    <TabLayout
      tabs={{
        ...tabs,
        active,
        onChangeTab,
      }}
      {...props}
    />
  );
};

export default TabLayout;
